import { apiInternals, send } from './client';

/**
 * `request()` is not itself exported from `client.ts` — only `send()` and
 * `apiInternals` are. `apiInternals.request` is the one non-`send()` path a
 * production module outside `client.ts` may use, and only for the reason
 * `api.auditLog` (and `devices.ts`'s `sensorsApi`/`readersApi`) state there:
 * reading `meta` as well as `data`.
 */
const { request } = apiInternals;

/**
 * The dispensary code pool (M-05) — the per-dispensary stock of reader and
 * sensor CODES (not the devices themselves; see `devices.ts` for those) that
 * get bound to a patient at dispense time.
 *
 * A separate module from `devices.ts`, `reference.ts` and `client.ts`,
 * mirroring all three: a different audience (`code.manage` — required on
 * every one of these five endpoints, including the reads, unlike
 * `devices.ts`'s open GETs) and a different lifetime.
 *
 * ## `list` is built on `request`, never `send`
 *
 * `GET /dispensary-codes` is CURSOR-PAGINATED — `{ data, meta: {
 * next_cursor, per_page } }` — exactly `DevicePage`/`AuditPage`'s shape.
 * `send()` resolves to `request()`'s `.data` ALONE and throws `meta` away; a
 * `list` written as `send<CodePage>(...)` would compile (the cast inside
 * `request()` is unchecked) but resolve to a bare `DispensaryCode[]` with no
 * `.meta` on it at all — the UI's "next page" control would have nothing to
 * read and would simply never appear, with no error anywhere. See
 * `devices.ts`'s own docblock and M-01c's report for this exact defect
 * shipped once already.
 *
 * ## `kind` + `type_code` — the legal pairing is NOT enforced at the type level
 *
 * `reader` codes are only ever `libre_2`/`libre_3`; `sensor` codes are only
 * ever `libre_2`/`libre_2_plus`/`libre_3_plus`. `CodeInput` (the `POST` body)
 * IS a discriminated union on `kind` so a caller cannot even compile
 * `{ kind: 'reader', type_code: 'libre_3_plus' }` — a pairing the API's own
 * 422 refuses. `CodeQuery` (the `GET` filter) deliberately is NOT narrowed
 * the same way: `kind` and `type` are two independent, optional filters (a
 * caller may legitimately filter by `type` alone, with no `kind` given at
 * all), and a discriminated union would force the two to travel together.
 * The illegal-pairing 422 is still the API's to raise for a filter that
 * makes no sense; nothing is lost by not modelling it here, since a bad
 * filter combination just yields an empty page rather than writing bad data.
 *
 * ## `type` on the way out, `type_code` on the way back
 *
 * `GET /dispensary-codes`'s query parameter is `type`; the resource itself,
 * and `POST /dispensary-codes`'s body, both use `type_code`. Not a typo —
 * the same read/write asymmetry `reference.ts` documents for
 * `dispensary_uuid` (read) vs `dispensary_id` (write), and `devices.ts` for
 * `type` (read) vs `sensor_type`/`reader_type` (write): the field is named
 * to match what the server actually accepts on that particular wire.
 *
 * ## The import is a REAL `multipart/form-data` upload, and there is no preview
 *
 * `POST /dispensary-codes/import` follows `devices.ts`'s import functions —
 * a genuine `FormData` body, never a JSON string field the way
 * `reference.ts`'s `centresImportApi` sends its CSV. Unlike `devices.ts`'s
 * import, this endpoint has no dry run: it only ever inserts, and a
 * duplicate `(dispensary, code, kind, type_code)` is counted and skipped,
 * never overwritten. There is deliberately no `preview`/`apply` pair and no
 * `preview_token` field here — modelling one would imply a step the API does
 * not have.
 *
 * ## `rejected` is a keyed OBJECT, never an array
 *
 * `CodeImportResult.rejected` is `Record<string, string>` — 1-based row
 * number to a rejection reason — and is `{}`, not `[]`, when nothing failed.
 * The API had a defect where an all-success import serialised this as `[]`
 * (an empty PHP array and an empty PHP associative array `json_encode` the
 * same way); that has been fixed API-side, but typing this field as
 * `readonly string[]` here would silently accept the old, ambiguous shape
 * back if the defect ever returned. Typed as a record so a caller reading
 * `result.rejected['3']` gets a real compile error the day this drifts.
 */

/** The two device families a code pool covers. */
export type CodeKind = 'reader' | 'sensor';

/** The `device_models.code` values a READER code may legally carry. */
export type ReaderTypeCode = 'libre_2' | 'libre_3';

/** The `device_models.code` values a SENSOR code may legally carry. */
export type SensorTypeCode = 'libre_2' | 'libre_2_plus' | 'libre_3_plus';

/** Every `device_models.code` value this pool sees, across both kinds. */
export type CodeTypeCode = ReaderTypeCode | SensorTypeCode;

/**
 * `uuid` + `name_en` — never the integer foreign key, the same convention
 * `DeviceDispensaryRef`/`reference.ts`'s refs follow.
 *
 * Verified field-for-field against
 * `app/Http/Resources/Code/DispensaryCodeResource.php:38-41` in the API
 * repository: the WHOLE `dispensary` key is `null` when the relation was
 * LOADED but resolved to no row — a soft-deleted dispensary (M-05
 * whole-branch review, F2/F4), not the ordinary case. CORRECTED HERE
 * (F4, whole-branch review): a relation that was NOT eager-loaded omits
 * the key entirely rather than sending it as `null`
 * (`Illuminate\Http\Resources\Json\JsonResource::whenLoaded()`'s own
 * semantics — the API repository previously documented this backwards) —
 * but `DispensaryCodeController::index()`/`store()` both always load this
 * relation, so the key itself is never actually omitted on the wire
 * today; only its value can be `null`. `CodeAvailabilityRow`
 * builds its `dispensary` differently — the object itself is always
 * present there, with `uuid`/`name_en` individually nullable instead — so
 * it does NOT reuse this type; see that interface's own docblock.
 */
export interface CodeDispensaryRef {
  uuid: string;
  name_en: string;
  name_ar: string;
}

/**
 * One code in the pool, exactly as `GET /dispensary-codes` renders it —
 * verified field-for-field against
 * `app/Http/Resources/Code/DispensaryCodeResource.php:33-44` in the API
 * repository, not inferred from the task brief's prose (the brief listed
 * `state` under the endpoint's QUERY parameters; carrying it into the row
 * shape too was this module's own mistake, caught in review).
 *
 * THERE IS NO `state` FIELD ON THE WIRE. Free/bound is never sent as a
 * literal — it is derived from `bound_at`, the one place `DispensaryCode
 * ::isBound()` (API-side) defines what "free" means. Re-introducing a
 * stored `state` field here would give the client and the server two
 * facts that can drift, exactly what M-05's own decision C-2 spent itself
 * avoiding. Use `codeState()` (below) to derive it, or read `bound_at`
 * directly.
 *
 * `updated_at` DOES NOT EXIST on this resource — only `created_at`. Both
 * `dispensary` and `created_at` are nullable: `created_at` follows the
 * resource's own `$this->created_at?->toIso8601String()`, defensive
 * against a row created without the timestamp ever being backfilled.
 */
export interface DispensaryCode {
  uuid: string;
  dispensary: CodeDispensaryRef | null;
  kind: CodeKind;
  type_code: CodeTypeCode;
  code: string;
  /** ISO 8601, or `null` when the code is still free. */
  bound_at: string | null;
  created_at: string | null;
}

/**
 * Derives "is this code free" the same way the API's own
 * `DispensaryCode::isBound()` does — from `bound_at`, never from a stored
 * flag. Exported so Task 7's UI never needs to re-derive this itself and
 * risk disagreeing with the server about what "free" means (see
 * `DispensaryCode`'s own docblock).
 */
export function codeState(row: Pick<DispensaryCode, 'bound_at'>): 'free' | 'bound' {
  return row.bound_at === null ? 'free' : 'bound';
}

/**
 * Every filter `GET /dispensary-codes` accepts. `type`, not `type_code` —
 * see this module's own docblock. All optional; an empty query lists every
 * free-and-bound code across every dispensary the caller may see.
 */
export interface CodeQuery {
  dispensary?: string;
  kind?: CodeKind;
  type?: CodeTypeCode;
  state?: 'free' | 'bound';
  cursor?: string;
  per_page?: number;
}

/** One page of the pool — cursor-paginated, exactly `DevicePage`/`AuditPage`'s shape. */
export interface CodePage {
  data: readonly DispensaryCode[];
  meta: {
    next_cursor: string | null;
    per_page: number;
  };
}

/**
 * One row of `GET /dispensary-codes/availability` — a FREE-count only;
 * bound codes are deliberately not counted here, since a bound code is
 * already committed to a patient and is of no interest to "how many can I
 * still hand out".
 *
 * `dispensary` is verified against
 * `DispensaryCodeController::availability():158-161` in the API
 * repository: built as `['uuid' => $dispensary?->uuid, 'name_en' =>
 * $dispensary?->name_en]` — the OBJECT itself is always present (unlike
 * `DispensaryCode.dispensary`, which can be `null` outright), but its two
 * fields are each independently nullable, since the grouped aggregate can
 * reference a `dispensary_id` whose row lookup misses.
 */
export interface CodeAvailabilityRow {
  dispensary: {
    uuid: string | null;
    name_en: string | null;
    name_ar: string | null;
  };
  kind: CodeKind;
  type_code: CodeTypeCode;
  free_count: number;
}

/**
 * `POST /dispensary-codes`'s body. A discriminated union on `kind` so the
 * legal `(kind, type_code)` pairing is enforced at compile time — see this
 * module's own docblock for why `CodeQuery` deliberately is NOT narrowed the
 * same way.
 */
export type CodeInput =
  | { dispensary_id: string; code: string; kind: 'reader'; type_code: ReaderTypeCode }
  | { dispensary_id: string; code: string; kind: 'sensor'; type_code: SensorTypeCode };

/**
 * `POST /dispensary-codes/import`'s response. `rejected` is a record keyed
 * by 1-based row number, `{}` when nothing failed — see this module's own
 * docblock on why this is not `readonly string[]`.
 */
export interface CodeImportResult {
  created: number;
  duplicate: number;
  rejected: Readonly<Record<string, string>>;
}

/**
 * `?key=value&...`, or `''` when every value is absent. `undefined` is
 * dropped rather than becoming the literal string `'undefined'` in the URL —
 * the identical helper `devices.ts`/`client.ts` each redeclare locally for
 * the same reason (neither is exported).
 */
function queryString(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, string | number | undefined][]) {
    if (value === undefined) continue;
    search.set(key, String(value));
  }
  const built = search.toString();
  return built === '' ? '' : `?${built}`;
}

export const codesApi = {
  /**
   * DELIBERATELY NOT `send<CodePage>()` — see this module's own docblock for
   * why that would compile and still lose `meta.next_cursor`.
   */
  list: async (query: CodeQuery = {}): Promise<CodePage> => {
    const { data, meta } = await request<readonly DispensaryCode[]>(
      `/dispensary-codes${queryString(query)}`,
    );
    return { data, meta: meta as CodePage['meta'] };
  },

  /** Free-count-only rows, one per `(dispensary, kind, type_code)`. Not paginated. */
  availability: () => send<readonly CodeAvailabilityRow[]>('/dispensary-codes/availability'),

  create: (input: CodeInput) =>
    send<DispensaryCode>('/dispensary-codes', { method: 'POST', body: input }),

  /**
   * Correct a free code.
   *
   * `Partial<CodeInput>` and not `CodeInput`: every field on the endpoint is
   * optional and an omitted one keeps its stored value, which is the whole
   * point — a `PATCH` that sent all four would turn "fix this typo" into
   * "retype the record". The union is flattened by `Partial` here, so a
   * caller CAN name a `(kind, type_code)` pair the union would have refused;
   * the server checks that pairing against the merged record and answers a
   * `422` naming `type_code` when it is wrong, which is the same check
   * `create` relies on for the pairs its own union cannot express either
   * (one half sent, the other stored).
   *
   * `409` when the code is bound, the same way `remove` below is refused and
   * for a stronger reason: rewriting a bound code in place would make the
   * record say a patient received a voucher they never did. Distinguishable
   * by `status`, never by `code` alone.
   */
  update: (uuid: string, input: Partial<CodeInput>) =>
    send<DispensaryCode>(`/dispensary-codes/${uuid}`, { method: 'PATCH', body: input }),

  /**
   * `409` when the code is bound — a bound code is the record of what a
   * patient was given, so the API refuses to delete it outright rather than
   * answering the same 422 a duplicate-on-create gets. Both throw the same
   * `ApiError`, distinguishable by `status` (see `client.ts`'s own
   * `ApiError`), never by `code` alone.
   */
  remove: (uuid: string) => send<void>(`/dispensary-codes/${uuid}`, { method: 'DELETE' }),

  /**
   * A REAL `multipart/form-data` upload — see this module's own docblock.
   * No preview/apply pair and no `preview_token`: this endpoint only ever
   * inserts, and a duplicate is counted and skipped, never overwritten.
   */
  import: (file: File, dispensaryUuid: string): Promise<CodeImportResult> => {
    const body = new FormData();
    body.set('dispensary_id', dispensaryUuid);
    body.set('file', file);
    return send<CodeImportResult>('/dispensary-codes/import', { method: 'POST', body });
  },
};
